import { createEffect, createSignal, onCleanup, type Accessor } from "solid-js";
import { api, type TaskQueueEvent } from "../shared";
import { getSelectedTargetProfileId } from "../runtime";

export type TaskQueueMonitoringMode = "websocket" | "manual" | "poll-5" | "poll-15" | "poll-30" | "poll-60";
export type TaskQueueConnectionState = "connecting" | "connected" | "catching_up" | "disconnected" | "unavailable" | "manual" | "polling";

const MODE_KEY = "searchgrid-task-queue-monitor-mode-v1";
const CURSOR_KEY = "searchgrid-task-queue-monitor-cursor-v1";
export const TASK_QUEUE_MONITOR_BACKOFF_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000] as const;

export function taskQueueMonitorRetryDelay(attempt: number, random = Math.random): number | null {
  const base = TASK_QUEUE_MONITOR_BACKOFF_MS[attempt - 1];
  return base == null ? null : Math.round(base * (0.8 + random() * 0.4));
}

export function isTaskQueueMonitorRetryable(error: unknown): boolean {
  return (error as { retryable?: boolean } | null)?.retryable !== false;
}

export function loadTaskQueueMonitoringMode(): TaskQueueMonitoringMode {
  const value = localStorage.getItem(MODE_KEY);
  return value === "manual" || value === "poll-5" || value === "poll-15" || value === "poll-30" || value === "poll-60"
    ? value
    : "websocket";
}

export function persistTaskQueueMonitoringMode(mode: TaskQueueMonitoringMode) {
  localStorage.setItem(MODE_KEY, mode);
}

export function useTaskQueueMonitor(input: {
  mode: Accessor<TaskQueueMonitoringMode>;
  scope?: Accessor<unknown>;
  onBootstrap: () => Promise<number>;
  onIncremental: (after: number) => Promise<{ changeCursor: number; resyncRequired?: boolean }>;
  /** Return true when the pushed event fully reconciled the active view. */
  onEvent?: (event: TaskQueueEvent) => boolean | void;
}) {
  const [state, setState] = createSignal<TaskQueueConnectionState>("connecting");
  const [lastEventAt, setLastEventAt] = createSignal<string | null>(null);
  const [lastSnapshotAt, setLastSnapshotAt] = createSignal<string | null>(null);
  const [message, setMessage] = createSignal("");
  const [attempt, setAttempt] = createSignal(0);
  const [nextRetryAt, setNextRetryAt] = createSignal<string | null>(null);
  const [retryRevision, setRetryRevision] = createSignal(0);
  const cursorKey = `${CURSOR_KEY}:${getSelectedTargetProfileId()}`;
  let cursor = Math.max(0, Number(localStorage.getItem(cursorKey) ?? "0"));

  const persistCursor = (value: number) => {
    cursor = Math.max(0, value);
    localStorage.setItem(cursorKey, String(cursor));
  };

  createEffect(() => {
    const mode = input.mode();
    input.scope?.();
    retryRevision();
    persistTaskQueueMonitoringMode(mode);
    let disposed = false;
    let socket: WebSocket | null = null;
    let timer: number | undefined;
    let stableTimer: number | undefined;
    let refreshTimer: number | undefined;
    let refreshing = false;
    let pendingIncremental = false;
    let failures = 0;

    const clearTimers = () => {
      if (timer !== undefined) window.clearTimeout(timer);
      if (stableTimer !== undefined) window.clearTimeout(stableTimer);
      if (refreshTimer !== undefined) window.clearTimeout(refreshTimer);
      timer = stableTimer = refreshTimer = undefined;
    };

    const markSuccess = () => {
      failures = 0;
      setAttempt(0);
      setNextRetryAt(null);
      setLastSnapshotAt(new Date().toISOString());
    };

    const stopUnavailable = (reason: string) => {
      clearTimers();
      socket?.close();
      socket = null;
      setState("unavailable");
      setNextRetryAt(null);
      setMessage(`${reason} Automatic retries stopped.`);
    };

    const scheduleFailure = (reason: string, work: () => void) => {
      failures += 1;
      setAttempt(failures);
      const delay = taskQueueMonitorRetryDelay(failures);
      if (delay == null) {
        stopUnavailable(reason);
        return;
      }
      setState("disconnected");
      setNextRetryAt(new Date(Date.now() + delay).toISOString());
      setMessage(`${reason} Retry ${failures + 1} of ${TASK_QUEUE_MONITOR_BACKOFF_MS.length} is scheduled.`);
      timer = window.setTimeout(work, delay);
    };

    const bootstrap = async () => {
      try {
        const nextCursor = await input.onBootstrap();
        if (disposed) return false;
        persistCursor(nextCursor);
        markSuccess();
        return true;
      } catch (error) {
        if (disposed) return false;
        const reason = error instanceof Error ? error.message : "Task queue snapshot failed.";
        if (!isTaskQueueMonitorRetryable(error)) stopUnavailable(reason);
        else scheduleFailure(reason, () => void start());
        return false;
      }
    };

    const incremental = async () => {
      if (refreshing || disposed) {
        pendingIncremental = true;
        return true;
      }
      refreshing = true;
      pendingIncremental = false;
      try {
        const result = await input.onIncremental(cursor);
        if (disposed) return false;
        if (result.resyncRequired) {
          const nextCursor = await input.onBootstrap();
          if (disposed) return false;
          persistCursor(nextCursor);
        } else {
          persistCursor(result.changeCursor);
        }
        markSuccess();
      } catch (error) {
        if (disposed) return false;
        pendingIncremental = false;
        const reason = error instanceof Error ? error.message : "Task queue update failed.";
        if (!isTaskQueueMonitorRetryable(error)) stopUnavailable(reason);
        else scheduleFailure(reason, mode.startsWith("poll-") ? () => void poll() : () => void incremental());
        return false;
      } finally {
        refreshing = false;
      }
      if (pendingIncremental && !disposed) void incremental();
      return true;
    };

    const scheduleIncremental = () => {
      pendingIncremental = true;
      if (refreshTimer !== undefined) window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => void incremental(), 150);
    };

    const connect = async () => {
      if (disposed) return;
      setState(failures ? "catching_up" : "connecting");
      setMessage(failures ? "Reconnecting to live task updates..." : "Opening live task stream...");
      try {
        const { ticket } = await api.taskQueue.streamTicket();
        if (disposed) return;
        socket = new WebSocket(api.taskQueue.streamUrl(ticket, cursor));
        socket.onopen = () => {
          setState("connected");
          setMessage("Live updates connected.");
          stableTimer = window.setTimeout(markSuccess, 30_000);
        };
        socket.onmessage = (event) => {
          const value = JSON.parse(String(event.data)) as any;
          if (value.type === "task-queue.event" && value.event) {
            setLastEventAt(value.event.createdAt ?? new Date().toISOString());
            const queueEvent = value.event as TaskQueueEvent;
            const reconciledFromPush = input.onEvent?.(queueEvent) === true;
            if (reconciledFromPush && !pendingIncremental && !refreshing) {
              persistCursor(queueEvent.id);
              markSuccess();
            } else {
              scheduleIncremental();
            }
          } else if (value.type === "task-queue.resync_required") {
            persistCursor(Number(value.cursor ?? 0));
            setState("catching_up");
            setMessage("Refreshing after task event history changed...");
            void bootstrap();
          }
        };
        socket.onclose = () => {
          if (disposed || state() === "unavailable") return;
          scheduleFailure("Live stream disconnected.", () => void connect());
        };
        socket.onerror = () => socket?.close();
      } catch (error) {
        if (disposed) return;
        const reason = error instanceof Error ? error.message : "Unable to open live task stream.";
        if (!isTaskQueueMonitorRetryable(error)) stopUnavailable(reason);
        else scheduleFailure(reason, () => void connect());
      }
    };

    const poll = async () => {
      if (disposed) return;
      const succeeded = await incremental();
      if (!succeeded || disposed || state() === "unavailable") return;
      const seconds = Number(mode.slice(5));
      timer = window.setTimeout(poll, seconds * 1_000);
    };

    const start = async () => {
      clearTimers();
      const ready = await bootstrap();
      if (!ready || disposed) return;
      if (mode === "manual") {
        setState("manual");
        setMessage("Updates only when you refresh.");
      } else if (mode.startsWith("poll-")) {
        setState("polling");
        setMessage(`Refreshing changes every ${Number(mode.slice(5))} seconds.`);
        timer = window.setTimeout(poll, Number(mode.slice(5)) * 1_000);
      } else {
        await connect();
      }
    };

    void start();
    onCleanup(() => {
      disposed = true;
      clearTimers();
      socket?.close();
    });
  });

  return {
    state,
    lastEventAt,
    lastSnapshotAt,
    message,
    attempt,
    nextRetryAt,
    retryNow: () => setRetryRevision((value) => value + 1),
    markSnapshot: (nextCursor?: number) => {
      if (nextCursor != null) persistCursor(nextCursor);
      setLastSnapshotAt(new Date().toISOString());
    },
  };
}
